import { Controller, useForm } from 'react-hook-form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  TEST_QUESTION_SOURCE,
  TEST_QUESTION_SOURCE_OPTIONS,
} from '../constants/test-question-source';
import {
  TEST_LIMIT_CAP_BY_PERIOD,
  TEST_PERIOD_NOUN,
  TEST_PERIOD_OPTIONS,
} from '../constants/test-period';
import { Card, CardContent } from './ui/card';
import { LoadingSpinner } from './ui/loading-spinner';
import { useWorkspace } from '../context/workspace-context';
import {
  type OrganizationSettings as OrganizationSettingsType,
  useOrganizationSettings,
  useUpdateOrganizationSettings,
} from '../hooks/use-organization';
import { useToast } from './ui/toast';

export default function OrganizationSettings() {
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();

  if (isLoadingWorkspaces) {
    return <LoadingSpinner />;
  }

  if (!currentWorkspace) {
    return <p className="text-sm text-muted-foreground">No workspace found</p>;
  }

  return <OrganizationSettingsForm organizationId={currentWorkspace.id} />;
}

function OrganizationSettingsForm({
  organizationId,
}: {
  organizationId: number;
}) {
  const { data: settings, isLoading } = useOrganizationSettings(organizationId);
  const updateSettings = useUpdateOrganizationSettings(organizationId);
  const { showToast } = useToast();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<OrganizationSettingsType>({
    values: settings,
  });

  const testsEnabled = watch('testsEnabled');
  const timedTestsEnabled = watch('timedTestsEnabled');
  const testLimitsEnabled = watch('testLimitsEnabled');
  const testQuestionSource = watch('testQuestionSource');
  const testPeriod = watch('testPeriod');
  const limitCap = TEST_LIMIT_CAP_BY_PERIOD[testPeriod];
  const limitCapMessage = `Must be at most ${limitCap} per ${TEST_PERIOD_NOUN[testPeriod]}`;
  const usesCompletedLevels =
    testQuestionSource === TEST_QUESTION_SOURCE.COMPLETED_LEVELS;

  const onSubmit = (data: OrganizationSettingsType) => {
    updateSettings.mutate(data, {
      onSuccess: () => showToast('Settings saved', 'success'),
      onError: () => showToast('Failed to save settings', 'error'),
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Card className="border shadow-none rounded-lg">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-medium">Organization Info</h3>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-xs">
                Name
              </Label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                className="h-9 text-sm"
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="industry" className="text-xs">
                Industry
              </Label>
              <Input
                id="industry"
                {...register('industry', { required: 'Industry is required' })}
                className="h-9 text-sm"
              />
              {errors.industry && (
                <p className="text-xs text-red-500">
                  {errors.industry.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="country" className="text-xs">
              Country
            </Label>
            <Input
              id="country"
              {...register('country', { required: 'Country is required' })}
              className="h-9 text-sm"
            />
            {errors.country && (
              <p className="text-xs text-red-500">{errors.country.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="about" className="text-xs">
              About
            </Label>
            <Textarea
              id="about"
              {...register('about', { required: 'About is required' })}
              className="text-sm min-h-[160px]"
            />
            {errors.about && (
              <p className="text-xs text-red-500">{errors.about.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border shadow-none rounded-lg">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-medium">Test Mode</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Enable Tests</p>
              <p className="text-xs text-muted-foreground">
                Let users take tests with questions from their courses
              </p>
            </div>
            <Switch
              checked={testsEnabled}
              onCheckedChange={(checked: boolean) =>
                setValue('testsEnabled', checked, { shouldDirty: true })
              }
              className="data-[state=checked]:bg-green-500"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="questionSource" className="text-xs">
              Include questions based on
            </Label>
            <Controller
              control={control}
              name="testQuestionSource"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="questionSource"
                    className="h-9 text-sm sm:max-w-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEST_QUESTION_SOURCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="questionCount" className="text-xs">
                Questions per session
              </Label>
              <Input
                id="questionCount"
                type="number"
                min={10}
                max={100}
                {...register('testQuestionCount', {
                  valueAsNumber: true,
                  min: { value: 10, message: 'Must be at least 10' },
                  max: { value: 100, message: 'Must be at most 100' },
                })}
                className="h-9 text-sm"
              />
              {errors.testQuestionCount && (
                <p className="text-xs text-red-500">
                  {errors.testQuestionCount.message}
                </p>
              )}
            </div>
            {usesCompletedLevels && (
              <div className="space-y-1">
                <Label htmlFor="minLevels" className="text-xs">
                  Minimum levels to unlock
                </Label>
                <Input
                  id="minLevels"
                  type="number"
                  min={1}
                  {...register('testMinLevels', {
                    valueAsNumber: true,
                    min: { value: 1, message: 'Must be at least 1' },
                  })}
                  className="h-9 text-sm"
                />
                {errors.testMinLevels && (
                  <p className="text-xs text-red-500">
                    {errors.testMinLevels.message}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Test limits</p>
              <p className="text-xs text-muted-foreground">
                Require a minimum number of tests per week or month, cap the
                maximum, and send reminders
              </p>
            </div>
            <Switch
              checked={testLimitsEnabled}
              onCheckedChange={(checked: boolean) =>
                setValue('testLimitsEnabled', checked, { shouldDirty: true })
              }
              className="data-[state=checked]:bg-green-500"
            />
          </div>

          {testLimitsEnabled && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="minTestsPerPeriod" className="text-xs">
                    Minimum tests per period
                  </Label>
                  <Input
                    id="minTestsPerPeriod"
                    type="number"
                    min={1}
                    max={limitCap}
                    step={1}
                    {...register('testMinPerPeriod', {
                      valueAsNumber: true,
                      required: 'Minimum is required',
                      min: { value: 1, message: 'Must be at least 1' },
                      max: { value: limitCap, message: limitCapMessage },
                      validate: (minimum) =>
                        minimum <= getValues('testMaxPerPeriod') ||
                        'Minimum cannot be more than maximum',
                      deps: ['testMaxPerPeriod', 'testPeriod'],
                    })}
                    className="h-9 text-sm"
                  />
                  {errors.testMinPerPeriod && (
                    <p className="text-xs text-red-500">
                      {errors.testMinPerPeriod.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="maxTestsPerPeriod" className="text-xs">
                    Maximum tests per period
                  </Label>
                  <Input
                    id="maxTestsPerPeriod"
                    type="number"
                    min={1}
                    max={limitCap}
                    step={1}
                    {...register('testMaxPerPeriod', {
                      valueAsNumber: true,
                      required: 'Maximum is required',
                      min: { value: 1, message: 'Must be at least 1' },
                      max: { value: limitCap, message: limitCapMessage },
                      deps: ['testMinPerPeriod', 'testPeriod'],
                    })}
                    className="h-9 text-sm"
                  />
                  {errors.testMaxPerPeriod && (
                    <p className="text-xs text-red-500">
                      {errors.testMaxPerPeriod.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="testPeriod" className="text-xs">
                  Period
                </Label>
                <Controller
                  control={control}
                  name="testPeriod"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="testPeriod"
                        className="h-9 text-sm sm:max-w-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEST_PERIOD_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border shadow-none rounded-lg">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-medium">Timed Tests</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Enable Timed Tests</p>
              <p className="text-xs text-muted-foreground">
                Start a countdown when a test begins
              </p>
            </div>
            <Switch
              checked={timedTestsEnabled}
              onCheckedChange={(checked: boolean) =>
                setValue('timedTestsEnabled', checked, { shouldDirty: true })
              }
              className="data-[state=checked]:bg-green-500"
            />
          </div>

          {timedTestsEnabled && (
            <div className="space-y-1">
              <Label htmlFor="timeLimitSeconds" className="text-xs">
                Time Limit (seconds)
              </Label>
              <Input
                id="timeLimitSeconds"
                type="number"
                min={60}
                {...register('testTimeLimitSeconds', {
                  valueAsNumber: true,
                  validate: (value) =>
                    !Number.isNaN(value) || 'Time limit is required',
                  min: { value: 60, message: 'Must be at least 60 seconds' },
                })}
                className="h-9 text-sm"
              />
              {errors.testTimeLimitSeconds && (
                <p className="text-xs text-red-500">
                  {errors.testTimeLimitSeconds.message}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!isDirty || updateSettings.isPending}
          className="bg-accent-purple hover:bg-accent-purple/90 text-white text-sm px-6"
        >
          {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
